/**
 * Breath Motion 用の経過時間クロック（happa v1.1 Phase D）。
 *
 * `AppState.progress === 0` の idle 時にのみ駆動する silhouette 呼吸アニメ
 * （`applyBreath` の第 2 引数 `t` に渡す秒数）の時刻源。DETONATE 再生中は
 * pause() で凍結し、Reset 時に reset() で 0 秒に戻す。
 *
 * 副作用:
 * - `performance.now()` を参照する（pure ではない）。DOM / Canvas は参照しない。
 * - 時刻源を `Date.now()` ではなく `performance.now()` にしているのは、
 *   システム時計の跳び・逆行・NTP 補正による不連続を避けるため（rAF ループと
 *   同じ時刻源にそろえる）。
 *
 * pause / resume の要件:
 * - pause 中は `t` が pausedAt 時点の値で固定される（silhouette が凍結）。
 * - resume 時は pause 中の経過分を `startTime` に加算し、再開直後の `t` が
 *   pause 直前と連続するよう補正する（飛びを出さない）。
 *
 * 設計参照:
 * - `docs/happa-designtool.md` Phase D 仕様（Breath Motion）
 * - `AppState.breath`（state/types.ts）の振幅・周期パラメータは本クラスが
 *   提供する `t` に対して評価される。
 */
export class Clock {
  /**
   * 計測開始時刻（ms、`performance.now()` 基準）。
   * resume 時に pause 期間分だけ未来へシフトすることで、t の連続性を保つ。
   */
  private startTime = performance.now();

  /** pause 中フラグ。true の間 `t` は `pausedAt` 基準で固定される。 */
  private paused = false;

  /**
   * pause() が呼ばれた時刻（ms、`performance.now()` 基準）。
   * paused=true の間のみ意味を持つ。resume 時の startTime 補正にも使う。
   */
  private pausedAt = 0;

  /**
   * 現在の経過秒。pause 中は pausedAt 基準、それ以外は now() 基準。
   *
   * ミリ秒基準の差分を秒単位に変換して返す（`/ 1000`）。初回インスタンス化
   * 直後は限りなく 0 に近い値（呼び出し時点の performance.now 分解能依存）。
   */
  get t(): number {
    const nowMs = this.paused ? this.pausedAt : performance.now();
    return (nowMs - this.startTime) / 1000;
  }

  /**
   * startTime を現在時刻にリセットし、pause 状態を解除する。
   * DETONATE 後の Reset ボタン等で呼び出す想定。
   */
  reset(): void {
    this.startTime = performance.now();
    this.paused = false;
    this.pausedAt = 0;
  }

  /**
   * 時計を凍結する。呼び出し時点の `performance.now()` を pausedAt として記録し、
   * 以降 `t` は同値を返し続ける。二重 pause は no-op（既に凍結済みの値を保つ）。
   *
   * DETONATE 再生中は silhouette の呼吸を止めるためこちらを呼ぶ運用。
   */
  pause(): void {
    if (this.paused) return;
    this.paused = true;
    this.pausedAt = performance.now();
  }

  /**
   * 凍結を解除する。pause 中に経過した実時間（now - pausedAt）を `startTime` へ
   * 足し込むことで、pause 前後の `t` を連続にする（呼吸の飛びを出さない）。
   * 非 paused 状態での呼び出しは no-op。
   */
  resume(): void {
    if (!this.paused) return;
    const elapsedDuringPause = performance.now() - this.pausedAt;
    this.startTime += elapsedDuringPause;
    this.paused = false;
    this.pausedAt = 0;
  }
}
